// Detects when this tab is running a STALE front-end bundle (pre-deploy
// JavaScript) and reloads to the current build.
//
// Why: the pre-redesign event-submission path baked the start/end wall-clock in
// the BROWSER's zone rather than the venue's, so a stale tab — e.g. someone in
// Central time submitting for an Eastern venue — silently stored events an hour
// off (the bug the server-authoritative redesign fixes). A stale tab never
// sends the naive wall-clock the new server path relies on, and the server
// cannot recover the intended time from the already-baked instant. So the only
// defense is to stop a stale tab from submitting: detect staleness (the build
// id baked into the page at load vs. what the server now serves) and reload.
//
// Every function here FAILS OPEN: if the build id is unknown or the check
// errors, we never block submission (behaviour is then exactly as before).

const META_NAME = 'app-build'

// The build id baked into the HTML when THIS tab loaded — captured once at
// module init, so it does not move when the deployed build does.
const loadedBuild: string | null =
  document.querySelector(`meta[name="${META_NAME}"]`)?.getAttribute('content') ?? null

let reloadInitiated = false

// The server's CURRENT build id. Returns null on any error or if the request
// takes too long — never throws and never hangs a submit (fail open).
const FETCH_TIMEOUT_MS = 3000

const fetchCurrentBuild = async (): Promise<string | null> => {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const response = await fetch('/app_build', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
    if (!response.ok) return null
    const data = (await response.json()) as { build?: string }
    return data.build ?? null
  } catch {
    return null
  } finally {
    window.clearTimeout(timer)
  }
}

// True only when we can positively confirm the running bundle is older than
// what the server serves. Unknown/erroring -> false (fail open).
export const isStaleBuild = async (): Promise<boolean> => {
  if (!loadedBuild) return false
  const current = await fetchCurrentBuild()
  if (!current) return false
  return current !== loadedBuild
}

// Reload once if the bundle is stale. Returns true when a reload was initiated
// (callers gating an action should abort it). Never throws. The once-guard plus
// the fact that a reload fetches fresh HTML (whose baked id then matches the
// server) prevents reload loops.
export const reloadIfStale = async (): Promise<boolean> => {
  if (reloadInitiated) return true
  if (!(await isStaleBuild())) return false
  reloadInitiated = true
  window.location.reload()
  return true
}
